import { useCallback, useMemo, useState } from "react";
import { endOfMonth, startOfMonth } from "date-fns";
import {
  centerRangeAt,
  durationToSliderValue,
  getPresetById,
  getPresetMarkerValues,
  getPresetForDuration,
  panRangeByMs,
  rangeDurationMs,
  setRangeDurationAroundDate,
  sliderValueToDuration,
  snapDurationToNearestPreset,
  snapSliderValueToPreset,
  TIMELINE_PRESETS,
  zoomRangeAroundDate,
} from "../engine";

export function useTimelineViewport() {
  const [range, setRange] = useState(() => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  }));

  const durationMs = useMemo(() => rangeDurationMs(range), [range]);
  const activePreset = useMemo(
    () => getPresetForDuration(durationMs),
    [durationMs],
  );
  const presetMarkers = useMemo(() => getPresetMarkerValues(), []);
  const sliderValue = useMemo(
    () => durationToSliderValue(durationMs),
    [durationMs],
  );

  const applyPreset = useCallback((presetId) => {
    const preset = getPresetById(presetId);
    setRange(centerRangeAt(new Date(), preset.durationMs));
  }, []);

  const goToday = useCallback(() => {
    setRange((prev) => centerRangeAt(new Date(), rangeDurationMs(prev)));
  }, []);

  const shiftByFraction = useCallback((fraction) => {
    setRange((prev) => panRangeByMs(prev, rangeDurationMs(prev) * fraction));
  }, []);

  const panByMs = useCallback((deltaMs) => {
    setRange((prev) => panRangeByMs(prev, deltaMs));
  }, []);

  const zoomAround = useCallback((direction, anchorDate) => {
    setRange((prev) => zoomRangeAroundDate(prev, direction, anchorDate));
  }, []);

  const setSliderValue = useCallback((nextValue, anchorDate) => {
    const snappedValue = snapSliderValueToPreset(nextValue);
    const didSnapToMarker = Math.abs(snappedValue - nextValue) > 1e-3;

    setRange((prev) => {
      const safeAnchorDate =
        anchorDate ||
        new Date((prev.start.getTime() + prev.end.getTime()) / 2);
      const rawDuration = sliderValueToDuration(snappedValue);
      const nextDuration = didSnapToMarker
        ? snapDurationToNearestPreset(rawDuration)
        : rawDuration;
      return setRangeDurationAroundDate(prev, nextDuration, safeAnchorDate);
    });
  }, []);

  return {
    range,
    setRange,
    durationMs,
    presets: TIMELINE_PRESETS,
    presetMarkers,
    activePreset,
    sliderValue,
    setSliderValue,
    applyPreset,
    goToday,
    shiftByFraction,
    panByMs,
    zoomAround,
  };
}
